import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import utils from "@utils";
import { AssignmentRulesService, JobService, OrganizationFavoriteGroupsService } from "@services";
import { AUTO_APPROVAL_TYPES, DAYS_OF_WEEK } from "@constants";
import { ReactSelect, Loader, Modal, Tooltip, TimeInput } from "../../../../../components";

import "./JobFormModals.scss";

const weekdays = DAYS_OF_WEEK.map(({ value, label }) => ({ value, label }));
class SendEstimateModal extends Component {
	constructor(props) {
		super(props);

		const { agencyAutoAssignPresets } = props;
		this.initialForm = {
			signUpPeriodHoursMultiplier: 1,
			timeToPost: "immediately",
			postAt: "PostPriorByDays",
			postUsingAssignmentGroup: false,
			postUsingStaggeredPosting: false,
			OpenToAllOfficers: false,
			postingTime: "",
			weekDayTime: "",
			monthDayTime: "",
			AssignmentGroupId: "",
			StaggeredRuleId: "",
			daysPriorForWeek: "",
			PostedType: 0,
			PostedDelayType: 0,
			PostDelayInMinutes: "",
			PostedGroup: -2,
			PostPriorByDays: "",
			PostByWeekDay: "0",
			PostByMonthDay: "",
			JobAutoAssignType: agencyAutoAssignPresets.JobAutoAssignType || 1,
			JobAutoAssignHoursSpan: agencyAutoAssignPresets.JobAutoAssignHoursSpan || "",
			JobAutoAssignYearToDateSpan:
				agencyAutoAssignPresets.JobAutoAssignYearToDateSpan || true,
			JobAutoAssignJobCountDaysSpan:
				agencyAutoAssignPresets.JobAutoAssignJobCountDaysSpan || "",
			JobAutoAssignQueueCycleMaxAssignments:
				agencyAutoAssignPresets.JobAutoAssignQueueCycleMaxAssignments || "",
		};
		this.state = {
			hideInBackground: false, // prevents opening two modals at the same time
			loadingData: true,
			officerGroups: [],
			assignmentGroups: [],
			staggeredRules: [],
			form: this.initialForm,
			errors: {
				AssignmentGroupId: false,
				StaggeredRuleId: false,
				PostedGroup: false,
				PostDelayInMinutes: false,
				postingTime: false,
				PostPriorByDays: false,
				PostByWeekDay: false,
				weekDayTime: false,
				daysPriorForWeek: false,
				PostByMonthDay: false,
				monthDayTime: false,
				JobAutoAssignHoursSpan: false,
				JobAutoAssignJobCountDaysSpan: false,
			},
		};
		this.signUpPeriodTimeTypeOptions = [
			{ label: "hours", value: 1 },
			{ label: "days", value: 24 },
		];
		const {
			FIRST_COME_FIRST_SERVED,
			MANUAL,
			SENIORITY_DESCENDING,
			SENIORITY_ASCENDING,
			BY_HOURS,
			BY_JOBS,
			RANDOM,
		} = AUTO_APPROVAL_TYPES;
		this.assignmentTypes = [
			{ label: "Manually", value: MANUAL },
			{ label: "First come first serve", value: FIRST_COME_FIRST_SERVED },
			{ label: "By hours", value: BY_HOURS },
			{ label: "By number of jobs", value: BY_JOBS },
			{ label: "Most senior", value: SENIORITY_DESCENDING },
			{ label: "Least senior", value: SENIORITY_ASCENDING },
			{ label: "Randomly ", value: RANDOM },
		];
		this.timesToPost = [
			{ label: "Now", value: "immediately" },
			{ label: "Based on job time", value: "basedOnJob" },
		];
	}

	componentDidMount() {
		const { jobId } = this.props;
		// Create promises that resolve with officer groups and assignment rules
		const promises = [
			new Promise(resolve => {
				OrganizationFavoriteGroupsService.getGroups()
					.then(({ data }) => {
						const result = data.map(({ Id: value, Name: label }) => ({ value, label }));
						result.unshift(
							{ value: -2, label: '"Entire department"' },
							{ value: -1, label: '"All officer groups"' },
						);
						resolve(result);
					})
					.catch(() => resolve([]));
			}),
		];
		promises.push(
			new Promise(resolve => {
				AssignmentRulesService.getAll()
					.then(({ data }) => {
						const result = data.map(({ Id: value, Name: label }) => ({ value, label }));
						resolve(result);
					})
					.catch(() => resolve([]));
			}),
		);
		promises.push(
			new Promise(resolve => {
				AssignmentRulesService.getStaggeredRules()
					.then(({ data }) => {
						const result = data.map(({ Id: value, Name: label }) => ({ value, label }));
						resolve(result);
					})
					.catch(() => resolve([]));
			}),
		);
		// If editing, also create a promise to retrieve the estimate settings available for the job
		if (jobId) {
			promises.push(
				new Promise(resolve => {
					const {
						form: { ...form },
					} = this.state;
					const newForm = Object.assign(form, {});

					JobService.getById(jobId)
						.then(({ data: jobEvent }) => {
							const cleanData = Object.fromEntries(
								Object.entries(jobEvent).filter(([, value]) =>
									value === 0 ? true : !!value,
								),
							);
							const postJobProps = Object.fromEntries(
								Object.entries(cleanData).filter(
									([key]) => Object.keys(form).indexOf(key) >= 0,
								),
							);

							Object.keys(postJobProps).forEach(property => {
								const value = postJobProps[property];

								if (property === "PostDelayInMinutes") {
									// parse PostDelayInMinutes to hours
									newForm[property] = value / 60;
								} else {
									newForm[property] = value;
								}

								// if JobAutoAssignHoursSpan are divisible by 24, is because is in days
								if (property === "JobAutoAssignHoursSpan" && value % 24 === 0) {
									newForm.signUpPeriodHoursMultiplier = 24;
									newForm[property] = value / 24;
								}
								// set Time to Post radio option
								if (
									property === "PostPriorByDays" ||
									property === "PostByWeekDay" ||
									property === "PostByMonthDay"
								) {
									newForm.postAt = property;
									newForm.timeToPost = "specificDate";

									let hours = Math.floor(postJobProps.PostTimeInMinutes / 60);
									const minutes = postJobProps.PostTimeInMinutes % 60;
									let suffix = "";
									if (utils.isDesktopSafari || utils.isIE) {
										suffix = " AM";
										// If hours >= 12, subtract 12 from the value and change the suffix to PM instead of AM
										if (hours >= 12) {
											hours -= 12;
											suffix = " PM";
										}
										// Whether it is 12 AM or 12 PM, an hours value of 0 is always equal to 12 o'clock
										if (hours === 0) {
											hours = 12;
										}
									}
									const timeToPost = `${hours < 10 ? 0 : ""}${hours}:${minutes}${suffix}`;
									// set time of active Time to Post option
									if (property === "PostPriorByDays") {
										newForm.postingTime = timeToPost;
									} else if (property === "PostByWeekDay") {
										newForm.weekDayTime = timeToPost;
										newForm.daysPriorForWeek = newForm.PostPriorByDays;
										newForm.PostPriorByDays = "";
									} else if (property === "PostByMonthDay") {
										newForm.monthDayTime = timeToPost;
									}
								}
							});
							resolve(newForm);
						})
						.catch(() => resolve(newForm));
				}),
			);
		}
		// Resolve all promises before disabling the loading state of the Post Job form
		// If editing a job, the form will be populated with the previous settings. Otherwise, it retains its initial values
		Promise.all(promises).then(([officerGroups, assignmentGroups, staggeredRules, form]) =>
			this.setState(prevState => ({
				officerGroups,
				assignmentGroups,
				staggeredRules,
				form: jobId ? form : prevState.form,
				loadingData: false,
			})),
		);
	}

	openAddContactModal = () => {
		const { openQuickAddContactModal, clientId, getContacts } = this.props;

		this.setState({ hideInBackground: true });

		openQuickAddContactModal("contacts", clientId, () => {
			getContacts(clientId);
			this.setState({ hideInBackground: false });
		});
	};

	/** Handle changes to radio button values
	 * @param {Event} e HTML event to handle and update state accordingly
	 */
	handleRadioChange = e => {
		const {
			form: { ...form },
		} = this.state;
		const {
			target: { name, value },
		} = e;

		form[name] = value;
		// clear conditional input values when toggling radio inputs
		if (name === "postUsingAssignmentGroup" && value === "false") {
			form.AssignmentGroupId = "";
		}
		if (name === "postUsingStaggeredPosting" && value === "false") {
			form.StaggeredRuleId = "";
		}
		if (name === "PostedType" && (parseInt(value, 10) === 0 || parseInt(value, 10) === 1)) {
			form.PostedGroup = "";
			form.PostedDelayType = "";
			form.PostDelayInMinutes = "";
		} else if (name === "PostedType" && parseInt(value, 10) === 2) {
			form.PostedDelayType = 0;
		}
		if (
			name === "PostedDelayType" &&
			(parseInt(value, 10) === 0 || parseInt(value, 10) === 2)
		) {
			form.PostDelayInMinutes = "";
		}
		if (name === "timeToPost" && value === "immediately") {
			form.postAt = "";
			form.postingTime = "";
			form.PostPriorByDays = "";
			form.PostByWeekDay = "";
			form.weekDayTime = "";
			form.daysPriorForWeek = "";
			form.PostByMonthDay = "";
			form.monthDayTime = "";
		}
		if (
			name === "postAt" ||
			name === "postUsingAssignmentGroup" ||
			name === "postUsingStaggeredPosting"
		) {
			switch (value) {
				case "PostPriorByDays":
					form.PostByWeekDay = "";
					form.weekDayTime = "";
					form.daysPriorForWeek = "";
					form.PostByMonthDay = "";
					form.monthDayTime = "";
					break;
				case "PostByWeekDay":
					form.postingTime = "";
					form.PostPriorByDays = "";
					form.PostByMonthDay = "";
					form.monthDayTime = "";
					break;
				case "PostByMonthDay":
					form.PostByMonthDay = "";
					form.monthDayTime = "";
					break;
				default:
					break;
			}
			switch (name) {
				case "postUsingStaggeredPosting":
					form.postUsingStaggeredPosting = value === "true";
					form.JobAutoAssignYearToDateSpan = false;
					form.postUsingAssignmentGroup = false;
					break;
				case "postUsingAssignmentGroup":
					form.postUsingAssignmentGroup = value === "true";
					form.JobAutoAssignYearToDateSpan = false;
					form.postUsingStaggeredPosting = false;
					break;
				default:
					break;
			}
		}
		if (
			name === "JobAutoAssignType" &&
			(parseInt(value, 10) === 1 || parseInt(value, 10) === 2)
		) {
			form.JobAutoAssignHoursSpan = "";
			form.signUpPeriodHoursMultiplier = 1;
			form.JobAutoAssignJobCountDaysSpan = "";
			form.JobAutoAssignYearToDateSpan = true;
		}
		if (name === "JobAutoAssignType" || name === "PostedType" || name === "PostedDelayType")
			form[name] = parseInt(value, 10);
		if (
			name === "JobAutoAssignYearToDateSpan" ||
			name === "postUsingAssignmentGroup" ||
			name === "postUsingStaggeredPosting"
		)
			form[name] = value === "true";

		this.setState({ form });
	};

	/** Handles changes to input values
	 * @param {Event} event HTML event to handle and update state accordingly
	 * @param {String} property An explicit property name to set the value for
	 */
	handleInputChange = (event, property) => {
		let value;
		let type;
		let name;
		let checked;
		if (
			property === "AssignmentGroupId" ||
			property === "StaggeredRuleId" ||
			property === "PostedGroup" ||
			property === "timeToPost" ||
			property === "JobAutoAssignType"
		) {
			value = event?.value || "";
			type = event?.type || "";
			name = event?.name || property;
			if (property === "PostedGroup") {
				let auxPostedType;
				if (value === -2) {
					auxPostedType = 0;
				} else if (value === -1) {
					auxPostedType = 1;
				} else {
					auxPostedType = 2;
				}
				this.setState(prevState => ({
					form: {
						...prevState.form,
						PostedType: auxPostedType,
						OpenToAllOfficers: false,
						PostedDelayType: null,
						PostDelayInMinutes: "",
					},
				}));
			}
			if (name === "timeToPost") {
				this.setState(prevState => ({
					form: {
						...prevState.form,
						postAt: "",
						postingTime: "",
						PostPriorByDays: "",
						PostByWeekDay: "",
						weekDayTime: "",
						daysPriorForWeek: "",
						PostByMonthDay: "",
						monthDayTime: "",
						PostExactDateTime: "",
						postExactTime: "",
					},
				}));
			}
		} else {
			({
				target: { value, type, name, checked },
			} = event);
		}

		if (type === "tel") {
			value = value.replace(/\D/g, "");
		}

		if (type === "checkbox") {
			value = checked;
			if (property === "OpenToAllOfficers") {
				if (!checked) {
					this.setState(prevState => ({
						form: { ...prevState.form, PostedDelayType: 0 },
					}));
				}
			}
		}

		this.setState(prevState => ({ form: { ...prevState.form, [name]: value } }));
	};

	/** Event handler for when an input containing an integer value is blurred (unfocused)
	 * @param {Event} event HTML event to handle and update state accordingly
	 */
	handleIntegerOnBlur = event => {
		const {
			target: { value, name },
		} = event;
		// OnBlur : set values to initial state if the value is less than 1
		if (name === "PostByMonthDay" && parseInt(value, 10) > 31) {
			this.setState(prevState => ({ form: { ...prevState.form, PostByMonthDay: "31" } }));
		} else if (parseInt(value, 10) < 1) {
			this.setState(prevState => ({ form: { ...prevState.form, [name]: "" } }));
		}
	};

	/** Determines if a time string is formatted correctly based on the browser the app is running in
	 * @param {String} time The time to be checked against a regular expression
	 */
	isTimeValid = time => {
		// If time is blank, it is not valid
		if (time === "") return false;

		let timeRegex = /(\d{2}):(\d{2})/;
		if (utils.isDesktopSafari || utils.isIE) {
			timeRegex = /(\d{2}):(\d{2}) (A|P)M/i;
		}
		return timeRegex.test(time);
	};

	/** Determines whether the current state of the Post Job form is valid for submission or not */
	validateForm = () => {
		const {
			form: {
				postUsingAssignmentGroup,
				postUsingStaggeredPosting,
				AssignmentGroupId,
				StaggeredRuleId,
				PostedType,
				PostedGroup,
				PostedDelayType,
				PostDelayInMinutes,
				timeToPost,
				postAt,
				postingTime,
				PostPriorByDays,
				PostByWeekDay,
				weekDayTime,
				daysPriorForWeek,
				PostByMonthDay,
				monthDayTime,
				JobAutoAssignType,
				JobAutoAssignHoursSpan,
				JobAutoAssignJobCountDaysSpan,
			},
		} = this.state;
		let { errors } = this.state;

		// Reset validations
		errors = Object.fromEntries(Object.entries(errors).map(([key]) => [key, false]));

		// Validate assignment rule selection
		if (postUsingAssignmentGroup && AssignmentGroupId !== "") return true;
		errors.AssignmentGroupId = postUsingAssignmentGroup && AssignmentGroupId === "";

		if (postUsingStaggeredPosting && StaggeredRuleId !== "") return true;
		errors.StaggeredRuleId = postUsingStaggeredPosting && StaggeredRuleId === "";

		// Validate officer group selection
		if (PostedType === 2) errors.PostedGroup = PostedGroup === "";
		errors.PostDelayInMinutes = PostedDelayType === 1 && PostDelayInMinutes === "";

		// Validate specificDate selections
		if (timeToPost === "specificDate") {
			switch (postAt) {
				case "PostPriorByDays":
					errors.postingTime = !this.isTimeValid(postingTime);
					errors.PostPriorByDays = PostPriorByDays === "";
					break;
				case "PostByWeekDay":
					errors.PostByWeekDay = PostByWeekDay === "";
					errors.weekDayTime = !this.isTimeValid(weekDayTime);
					errors.daysPriorForWeek = daysPriorForWeek === "";
					break;
				case "PostByMonthDay":
					errors.PostByMonthDay = PostByMonthDay === "";
					errors.monthDayTime = !this.isTimeValid(monthDayTime);
					break;
				default:
					return false;
			}
		}

		// Validate jobAutoAssign selections
		if (
			JobAutoAssignType === AUTO_APPROVAL_TYPES.BY_HOURS ||
			JobAutoAssignType === AUTO_APPROVAL_TYPES.BY_JOBS
		) {
			errors.JobAutoAssignHoursSpan =
				JobAutoAssignHoursSpan === "" || parseInt(JobAutoAssignHoursSpan, 10) === 0;
			errors.JobAutoAssignJobCountDaysSpan =
				JobAutoAssignJobCountDaysSpan === "" ||
				parseInt(JobAutoAssignJobCountDaysSpan, 10) === 0;
		}

		if (JobAutoAssignType === AUTO_APPROVAL_TYPES.RANDOM) {
			errors.JobAutoAssignHoursSpan =
				JobAutoAssignHoursSpan === "" || parseInt(JobAutoAssignHoursSpan, 10) === 0;
		}
		this.setState({ errors });

		// Returns false if any of the error values evaluate to a boolean `true`
		return !Object.values(errors).some(val => val);
	};

	/** Format data object appropriately for request submission, omitting any unecessary values
	 * @return {Object} A formatted data object to be submitted to the API
	 */
	prepareData = () => {
		const {
			form: { ...form },
		} = this.state;
		const { MANUAL, FIRST_COME_FIRST_SERVED } = AUTO_APPROVAL_TYPES;
		const filteredData = Object.fromEntries(
			Object.entries(form).filter(([key]) => key[0] === key[0].toUpperCase()),
		);
		const { PostDelayInMinutes, PostedDelayType } = filteredData;

		// set scheduled posted values depending on type
		if (form.timeToPost === "specificDate") {
			switch (form.postAt) {
				case "PostPriorByDays":
					filteredData.PostTimeInMinutes = utils.convertTimeToMinutes(form.postingTime);
					filteredData.PostPriorByDays = parseInt(form.PostPriorByDays, 10);
					filteredData.PostByMonthDay = null;
					filteredData.PostByWeekDay = null;
					break;
				case "PostByWeekDay":
					filteredData.PostByWeekDay = parseInt(form.PostByWeekDay, 10);
					filteredData.PostTimeInMinutes = utils.convertTimeToMinutes(form.weekDayTime);
					filteredData.PostPriorByDays = parseInt(form.daysPriorForWeek, 10);
					filteredData.PostByMonthDay = null;
					break;
				case "PostByMonthDay":
					filteredData.PostByWeekDay = null;
					filteredData.PostPriorByDays = null;
					filteredData.PostByMonthDay = parseInt(form.PostByMonthDay, 10);
					filteredData.PostTimeInMinutes = utils.convertTimeToMinutes(form.monthDayTime);
					break;
				default:
					break;
			}
		} else {
			// if posting immediately, set specific posting properties to null
			filteredData.PostByMonthDay = null;
			filteredData.PostByWeekDay = null;
			filteredData.PostPriorByDays = null;
		}

		/** When PostDelayType is variable, we need to include the PostDelayInMinutes */
		if (PostedDelayType === 1) {
			filteredData.PostDelayInMinutes = parseInt(PostDelayInMinutes, 10) * 60;
		} else {
			filteredData.PostDelayInMinutes = null;
		}

		// if the sign up period is in days, calculate the equivalent hours
		if (form.signUpPeriodHoursMultiplier === 24) {
			filteredData.JobAutoAssignHoursSpan *= 24;
		}

		// Omit falsy values and parse number strings to integers
		const parsedData = {};
		Object.entries(filteredData).forEach(([key, val]) => {
			if (val !== undefined && val !== null) {
				parsedData[key] = Number.isNaN(parseInt(val, 10)) ? val || null : parseInt(val, 10);
			}
		});

		// clear group data if posting to all officers
		if (parsedData.PostedType === 0) {
			parsedData.PostedGroup = null;
			parsedData.PostedGroupName = null;
		}

		// make Auto Assign data null if manually or FCFS is selected
		const { JobAutoAssignType } = parsedData;
		if (JobAutoAssignType === MANUAL || JobAutoAssignType === FIRST_COME_FIRST_SERVED) {
			parsedData.JobAutoAssignHoursSpan =
				parsedData.JobAutoAssignType === FIRST_COME_FIRST_SERVED ? 0 : null;
			parsedData.JobAutoAssignJobCountDaysSpan = null;
			parsedData.JobAutoAssignQueueCycleMaxAssignments = null;
			parsedData.JobAutoAssignYearToDateSpan = null;
		}

		return parsedData;
	};

	/** Handles the "Cancel" button being pressed */
	onCancel = () => {
		const { closeModal } = this.props;
		closeModal();
	};

	/** Triggered once the modal has completely disappeared from the window to reset all state fields to appropriate default values */
	onExited = () => {
		const {
			agencyAutoAssignPresets: {
				JobAutoAssignType = 1,
				JobAutoAssignJobCountDaysSpan = "",
				JobAutoAssignYearToDateSpan = true,
			},
		} = this.props;
		let {
			agencyAutoAssignPresets: { JobAutoAssignHoursSpan = "" },
		} = this.props;
		let signUpPeriodHoursMultiplier = 1;
		if (JobAutoAssignHoursSpan && JobAutoAssignHoursSpan % 24 === 0) {
			signUpPeriodHoursMultiplier = 24;
			JobAutoAssignHoursSpan = parseInt(JobAutoAssignHoursSpan, 10) / 24;
		}

		this.setState({
			hideInBackground: false,
			form: {
				...this.initialForm,
				signUpPeriodHoursMultiplier,
				JobAutoAssignHoursSpan,
				JobAutoAssignType,
				JobAutoAssignJobCountDaysSpan,
				JobAutoAssignYearToDateSpan,
			},
			errors: {
				AssignmentGroupId: false,
				StaggeredRuleId: false,
				PostedGroup: false,
				PostDelayInMinutes: false,
				postingTime: false,
				PostPriorByDays: false,
				PostByWeekDay: false,
				weekDayTime: false,
				daysPriorForWeek: false,
				PostByMonthDay: false,
				monthDayTime: false,
				JobAutoAssignHoursSpan: false,
				JobAutoAssignJobCountDaysSpan: false,
			},
		});
	};

	/** Handles the "Save" button being pressed */
	handleSave = () => {
		const {
			invoiceEstimate: { ClientInvoiceEstimateContactId },
			handleJobCreation,
			closeModal,
		} = this.props;

		if (ClientInvoiceEstimateContactId === "") {
			utils.notify({
				type: "error",
				message: "No contact selected",
				timeOut: 1500,
			});
			return;
		}

		if (!this.validateForm()) {
			return;
		}

		const postOptions = this.prepareData();
		handleJobCreation(postOptions, true);
		closeModal();
	};

	render() {
		const { lang } = global;
		const {
			hideInBackground,
			loadingData,
			assignmentGroups,
			staggeredRules,
			officerGroups,
			form,
			errors,
		} = this.state;
		const {
			estimateTotal,
			showModal,
			closeModal,
			requirePrepayment,
			jobDate,
			clientContacts,
			invoiceEstimate,
			estimatePostOption,
			handleEstimateModalFormChange,
			user,
		} = this.props;
		const { SENIORITY_DESCENDING, SENIORITY_ASCENDING, BY_HOURS, BY_JOBS, RANDOM } =
			AUTO_APPROVAL_TYPES;

		const date = utils.formatDate(`${jobDate} 00:00:00`, undefined, undefined, "short");

		return (
			<Modal
				show={showModal && !hideInBackground}
				onHide={closeModal}
				onExited={() => {
					if (!hideInBackground) this.onExited();
				}}
				size="lg"
				id="send-estimate-modal"
			>
				<Modal.Header closeButton>
					<Modal.Title>{`${lang.t("Send")} ${lang.t("Estimate")}`}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<If condition={!requirePrepayment}>
						<div className="form-group">
							<div>
								<strong>{lang.t("Require Estimate Approval")}</strong>
								<Tooltip
									content={`
									${lang.t("Checking yes means you would like to send this job as an estimate to the client contact_")}
									${lang.t("If approval required, officer assignment will not be available until after estimate is approved_")}
									`}
								>
									<i className="rkf rkf-question-solid tooltip-icon" />
								</Tooltip>
							</div>
							<div className="radio">
								<label htmlFor="estimate-approval-not-required">
									<input
										type="radio"
										className="estimate-approval-radio"
										name="estimateApprovalRequired"
										id="estimate-approval-not-required"
										onChange={handleEstimateModalFormChange(
											"estimateApprovalRequired",
											false,
										)}
										checked={
											invoiceEstimate.RequireClientInvoiceEstimateApproval ===
											false
										}
										disabled={requirePrepayment}
									/>
									{lang.t("No")}
								</label>
							</div>
							<div className="radio">
								<label htmlFor="estimate-approval-required">
									<input
										type="radio"
										className="estimate-approval-radio"
										name="estimateApprovalRequired"
										id="estimate-approval-required"
										onChange={handleEstimateModalFormChange(
											"estimateApprovalRequired",
											true,
										)}
										checked={
											invoiceEstimate.RequireClientInvoiceEstimateApproval ===
											true
										}
										disabled={requirePrepayment}
									/>
									{lang.t("Yes")}
								</label>
							</div>
						</div>
					</If>
					<strong>{lang.t("You are about to send an estimate")}</strong>
					<div>
						<span className="estimate-info-label">{`${lang.t("Estimate")}:`}</span>
						{utils.currencyFormatter(estimateTotal)}
					</div>
					<div>
						<span className="estimate-info-label">{`${lang.t("Job Date")}:`}</span>
						{date}
					</div>
					<div className="form-group">
						<div>
							<label htmlFor="estimate-contact-id">{`${lang.t("Send estimate to")}:`}</label>
							<button
								type="button"
								id="add-contact"
								className="btn btn-link pull-right"
								onClick={this.openAddContactModal}
							>
								{`+ ${lang.t("New")}`}
							</button>
						</div>
						<ReactSelect
							id="estimate-contact-id"
							value={invoiceEstimate.ClientInvoiceEstimateContactId}
							name="ClientInvoiceEstimateContactId"
							onChange={handleEstimateModalFormChange("estimateContactId")}
							options={clientContacts}
							isLoading={false}
							isClearable
						/>
					</div>
					<br />
					<strong>{lang.t("When would you like to assign officers for the job_")}</strong>
					<div className="form-group">
						<div className="radio">
							<label htmlFor="estimate-post-option-manual">
								<input
									type="radio"
									name="estimatePostOption"
									id="estimate-post-option-manual"
									value="manual"
									onChange={handleEstimateModalFormChange("estimatePostOption")}
									checked={estimatePostOption === "manual"}
								/>
								{lang.t("I will post and or assign officers manually_")}
							</label>
						</div>
						<div className="radio">
							<label htmlFor="estimate-post-option-automatic">
								<input
									type="radio"
									name="estimatePostOption"
									id="estimate-post-option-automatic"
									value="automatic"
									onChange={handleEstimateModalFormChange("estimatePostOption")}
									checked={estimatePostOption === "automatic"}
								/>
								{lang.t("Automatically post and/or assign officers_")}
							</label>
						</div>
					</div>
					<If condition={estimatePostOption === "automatic"}>
						<div className="separator separator-solid" />
						<div className="assignment-form-container">
							<Loader active={loadingData} />
							<div className="col-xs-12">{lang.t("Post with posting rule")}</div>
							<div className="col-xs-12">
								<div className="form-group">
									<div className="radio">
										<label htmlFor="postUsingAssignmentGroup-no">
											<input
												type="radio"
												name="postUsingAssignmentGroup"
												id="postUsingAssignmentGroup-no"
												value="false"
												checked={
													!form.postUsingAssignmentGroup &&
													!form.postUsingStaggeredPosting
												}
												onChange={e => this.handleRadioChange(e)}
											/>
											{lang.t("No")}
										</label>
									</div>
									<div className="radio">
										<label htmlFor="postUsingAssignmentGroup-yes">
											<input
												type="radio"
												name="postUsingAssignmentGroup"
												id="postUsingAssignmentGroup-yes"
												value="true"
												checked={form.postUsingAssignmentGroup}
												onChange={e => this.handleRadioChange(e)}
											/>
											{lang.t("Use a single posting rule")}
										</label>
									</div>
									<div className="radio">
										<label htmlFor="postUsingStaggeredPosting-yes">
											<input
												type="radio"
												name="postUsingStaggeredPosting"
												id="postUsingStaggeredPosting-yes"
												value="true"
												checked={form.postUsingStaggeredPosting}
												onChange={e => this.handleRadioChange(e)}
											/>
											{lang.t("Use staggered posting")}
										</label>
									</div>
								</div>
							</div>
							<Choose>
								<When
									condition={
										form.postUsingAssignmentGroup ||
										form.postUsingStaggeredPosting
									}
								>
									<If condition={form.postUsingAssignmentGroup}>
										<div className="row">
											<div className="col-xs-12">
												<div
													className={classNames("form-group", {
														"has-error": errors.AssignmentGroupId,
													})}
												>
													<span>{lang.t("Assignment Rule Name")}</span>
													<button
														type="button"
														id="add-new-group"
														className="btn btn-link"
														onClick={() => this.setAssignmentRule()}
													>
														{`+ ${lang.t("Create New Rule")}`}
													</button>
													<ReactSelect
														isClearable
														options={assignmentGroups}
														name="AssignmentGroupId"
														value={form.AssignmentGroupId}
														onChange={e =>
															this.handleInputChange(
																e,
																"AssignmentGroupId",
															)
														}
													/>
												</div>
											</div>
										</div>
									</If>
									<If condition={form.postUsingStaggeredPosting}>
										<div className="row">
											<div className="col-xs-12">
												<div
													className={classNames("form-group", {
														"has-error": errors.StaggeredRuleId,
													})}
												>
													<span>{lang.t("Staggered Rule Name")}</span>
													<ReactSelect
														isClearable
														options={staggeredRules}
														name="StaggeredRuleId"
														value={form.StaggeredRuleId}
														onChange={e =>
															this.handleInputChange(
																e,
																"StaggeredRuleId",
															)
														}
													/>
												</div>
											</div>
										</div>
									</If>
								</When>
								<Otherwise>
									{/* Time to Consider */}
									<div className="row">
										<div
											className="col-xs-12"
											style={{ marginTop: "5px" }}
										>
											<div
												className={classNames("form-group", {
													"has-error": errors.AssignmentGroupId,
												})}
											>
												<span>{lang.t("Post Time")}</span>
												<ReactSelect
													options={this.timesToPost}
													name="timeToPost"
													value={form.timeToPost}
													onChange={e =>
														this.handleInputChange(e, "timeToPost")
													}
												/>
											</div>
										</div>
										<div className="col-xs-12">
											<If condition={form.timeToPost === "basedOnJob"}>
												<div className="indent">
													<div className="radio radio-align">
														<label htmlFor="PostPriorByDays">
															<input
																type="radio"
																name="postAt"
																id="PostPriorByDays"
																value="PostPriorByDays"
																checked={
																	form.postAt ===
																	"PostPriorByDays"
																}
																onChange={e =>
																	this.handleRadioChange(e)
																}
															/>
															{lang.t("Daily:")}
														</label>
													</div>
													<TimeInput
														name="postingTime"
														id="postingTime"
														time={form.postingTime}
														className={classNames("form-control", {
															"has-error": errors.postingTime,
														})}
														disabled={form.postAt !== "PostPriorByDays"}
														handleChange={e =>
															this.handleInputChange(e)
														}
														user={user}
													/>
													<input
														type="tel"
														className={classNames("form-control", {
															"has-error": errors.PostPriorByDays,
														})}
														placeholder="eg: 2"
														name="PostPriorByDays"
														value={form.PostPriorByDays}
														disabled={form.postAt !== "PostPriorByDays"}
														onChange={e => this.handleInputChange(e)}
														onBlur={e => this.handleIntegerOnBlur(e)}
													/>
													<span>
														{lang.t("days prior to the job start_")}
													</span>
												</div>

												<div className="indent">
													<div className="radio radio-align">
														<label htmlFor="PostByWeek">
															<input
																type="radio"
																name="postAt"
																id="PostByWeek"
																value="PostByWeekDay"
																checked={
																	form.postAt === "PostByWeekDay"
																}
																onChange={e =>
																	this.handleRadioChange(e)
																}
															/>
															{lang.t("Weekly:")}
														</label>
													</div>
													<select
														placeholder={lang.t("Select day")}
														className={classNames("form-control", {
															"has-error": errors.PostByWeekDay,
														})}
														name="PostByWeekDay"
														value={form.PostByWeekDay}
														disabled={form.postAt !== "PostByWeekDay"}
														onChange={e => this.handleInputChange(e)}
													>
														<option value="">
															{lang.t("Select a week day")}
														</option>
														<For
															each="item"
															of={weekdays}
															index="index"
														>
															<option
																key={`PostByWeekDay_days${index}_${item.value}`}
																value={item.value}
															>
																{item.label}
															</option>
														</For>
													</select>
													<span>{lang.t("At")}</span>
													<TimeInput
														name="weekDayTime"
														id="weekDayTime"
														time={form.weekDayTime}
														className={classNames("form-control", {
															"has-error": errors.weekDayTime,
														})}
														disabled={form.postAt !== "PostByWeekDay"}
														handleChange={e =>
															this.handleInputChange(e)
														}
														user={user}
													/>
													<input
														className={classNames("form-control", {
															"has-error": errors.daysPriorForWeek,
														})}
														type="tel"
														placeholder="eg: 2"
														name="daysPriorForWeek"
														value={form.daysPriorForWeek}
														disabled={form.postAt !== "PostByWeekDay"}
														onChange={e => this.handleInputChange(e)}
														onBlur={e => this.handleIntegerOnBlur(e)}
													/>
													<span>
														{lang.t("days prior to the job start_")}
													</span>
												</div>

												<div className="indent">
													<div className="radio radio-align">
														<label htmlFor="PostByMonth">
															<input
																type="radio"
																name="postAt"
																id="PostByMonth"
																value="PostByMonthDay"
																checked={
																	form.postAt === "PostByMonthDay"
																}
																onChange={e =>
																	this.handleRadioChange(e)
																}
															/>
															{lang.t("Monthly:")}
														</label>
													</div>
													<input
														name="PostByMonthDay"
														id="PostByMonthDay"
														className={classNames("form-control", {
															"has-error": errors.PostByMonthDay,
														})}
														type="tel"
														value={form.PostByMonthDay}
														disabled={form.postAt !== "PostByMonthDay"}
														onChange={e => this.handleInputChange(e)}
														onBlur={e => this.handleIntegerOnBlur(e)}
													/>
													<span>{lang.t("day of month at")}</span>
													<TimeInput
														name="monthDayTime"
														id="monthDayTime"
														time={form.monthDayTime}
														className={classNames("form-control", {
															"has-error": errors.monthDayTime,
														})}
														disabled={form.postAt !== "PostByMonthDay"}
														handleChange={e =>
															this.handleInputChange(e)
														}
														user={user}
													/>
													<span>{lang.t("all of next month jobs_")}</span>
												</div>
											</If>
										</div>
									</div>
									<If
										condition={
											form.timeToPost === "specificDate" ||
											form.timeToPost === "basedOnJob"
										}
									>
										<div className="separator separator-solid" />
									</If>
									<div className="row">
										<div className="col-xs-12">
											<div
												className={classNames("form-group", {
													"has-error": errors.AssignmentGroupId,
												})}
											>
												<span>{lang.t("Viewing group (audience)")}</span>
												<ReactSelect
													options={officerGroups}
													name="PostedGroup"
													value={form.PostedGroup}
													onChange={e =>
														this.handleInputChange(e, "PostedGroup")
													}
												/>
											</div>
										</div>
										<div className="col-xs-12">
											<If condition={form.PostedType === 2}>
												<div>
													<label htmlFor="OpenToAllOfficers">
														<input
															style={{ marginRight: "8px" }}
															type="checkbox"
															name="OpenToAllOfficers"
															id="OpenToAllOfficers"
															checked={form.OpenToAllOfficers}
															value={form.OpenToAllOfficers}
															onChange={e =>
																this.handleInputChange(
																	e,
																	"OpenToAllOfficers",
																)
															}
															onClick={e =>
																this.handleInputChange(
																	e,
																	"OpenToAllOfficers",
																)
															}
														/>
														{`${lang.t("Open to all officers")}`}
														<Tooltip
															content={`${lang.t("If this option is selected, you can post to a specific group then delay posting to the whole department with the two options below")}`}
														>
															<i className="rkf rkf-question-solid tooltip-icon hidden-xs" />
														</Tooltip>
													</label>
												</div>
												<If condition={form.OpenToAllOfficers}>
													<div style={{ marginLeft: "8px" }}>
														<div className="radio radio-align">
															<label htmlFor="PostedDelayType-variable">
																<input
																	type="radio"
																	name="PostedDelayType"
																	id="PostedDelayType-variable"
																	value={1}
																	checked={
																		form.PostedDelayType === 1
																	}
																	onChange={e =>
																		this.handleRadioChange(e)
																	}
																/>
																<span>{lang.t("After")}</span>
															</label>
														</div>
														<input
															type="tel"
															name="PostDelayInMinutes"
															className={classNames("form-control", {
																"has-error":
																	errors.PostDelayInMinutes,
															})}
															placeholder="eg: 2"
															disabled={form.PostedDelayType !== 1}
															value={form.PostDelayInMinutes}
															onChange={e =>
																this.handleInputChange(e)
															}
															onBlur={e =>
																this.handleIntegerOnBlur(e)
															}
														/>
														<span>{lang.t("Hours")}</span>
														<div className="radio">
															<label htmlFor="PostedDelayType-static">
																<input
																	type="radio"
																	name="PostedDelayType"
																	id="PostedDelayType-static"
																	value="2"
																	checked={
																		form.PostedDelayType === 2
																	}
																	onChange={e =>
																		this.handleRadioChange(e)
																	}
																/>
																{lang.t("If not filled")}{" "}
																<strong>
																	{lang.t(
																		"48 hours before job start_",
																	)}
																</strong>
															</label>
														</div>
													</div>
												</If>
											</If>
										</div>
									</div>
									{/* Post with Assignment Rule */}

									{/* Officer Approval & Assignment Rules */}
									<If condition={form.PostedType === 2}>
										<div className="separator separator-solid" />
									</If>
									<div className="row">
										<div className="col-xs-12">
											<div
												className={classNames("form-group", {
													"has-error": errors.AssignmentGroupId,
												})}
											>
												<span>{lang.t("Assignment Type")}</span>
												<ReactSelect
													options={this.assignmentTypes}
													name="JobAutoAssignType"
													value={form.JobAutoAssignType}
													onChange={e =>
														this.handleInputChange(
															e,
															"JobAutoAssignType",
														)
													}
												/>
											</div>
											<If condition={form.JobAutoAssignType === BY_HOURS}>
												<div className="indent">
													<span>{lang.t("Sign-Up Period")}:</span>
													<input
														className={classNames("form-control", {
															"has-error":
																errors.JobAutoAssignHoursSpan,
														})}
														type="tel"
														placeholder="eg: 2"
														name="JobAutoAssignHoursSpan"
														value={form.JobAutoAssignHoursSpan}
														onChange={e => this.handleInputChange(e)}
														onBlur={e => this.handleIntegerOnBlur(e)}
													/>
													<select
														name="signUpPeriodHoursMultiplier"
														className="form-control"
														placeholder={lang.t("Hours or days_")}
														value={form.signUpPeriodHoursMultiplier}
														onChange={e => this.handleInputChange(e)}
													>
														<For
															each="item"
															of={this.signUpPeriodTimeTypeOptions}
															index="index"
														>
															<option
																key={`signUpPeriod_byHours${index}_${item.value}`}
																value={item.value}
															>
																{item.label}
															</option>
														</For>
													</select>
													<div>{lang.t("Time to Consider")}:</div>
													<div className="radio radio-align">
														<label htmlFor="JobAutoAssignJobCountDaysRadio-byHours">
															<input
																type="radio"
																name="JobAutoAssignYearToDateSpan"
																id="JobAutoAssignJobCountDaysRadio-byHours"
																checked={
																	!form.JobAutoAssignYearToDateSpan
																}
																value="false"
																onChange={e =>
																	this.handleRadioChange(e)
																}
															/>
														</label>
													</div>
													<input
														className={classNames("form-control", {
															"has-error":
																errors.JobAutoAssignJobCountDaysSpan,
														})}
														type="tel"
														placeholder="eg: 2"
														name="JobAutoAssignJobCountDaysSpan"
														value={form.JobAutoAssignJobCountDaysSpan}
														disabled={form.JobAutoAssignYearToDateSpan}
														onChange={e => this.handleInputChange(e)}
														onBlur={e => this.handleIntegerOnBlur(e)}
													/>
													<span>{lang.t("days")}</span>
													<div className="radio">
														<label htmlFor="JobAutoAssignYearToDateSpan-toDate">
															<input
																type="radio"
																name="JobAutoAssignYearToDateSpan"
																id="JobAutoAssignYearToDateSpan-toDate"
																checked={
																	form.JobAutoAssignYearToDateSpan
																}
																value="true"
																onChange={e =>
																	this.handleRadioChange(e)
																}
															/>
															<span>{lang.t("Year to Date")}</span>
														</label>
													</div>
												</div>
											</If>
											<If condition={form.JobAutoAssignType === BY_JOBS}>
												<div className="indent">
													<span>{lang.t("Sign-Up Period")}:</span>
													<input
														className={classNames("form-control", {
															"has-error":
																errors.JobAutoAssignHoursSpan,
														})}
														type="tel"
														placeholder="eg: 2"
														name="JobAutoAssignHoursSpan"
														value={form.JobAutoAssignHoursSpan}
														onChange={e => this.handleInputChange(e)}
														onBlur={e => this.handleIntegerOnBlur(e)}
													/>
													<select
														name="signUpPeriodHoursMultiplier"
														className="form-control"
														placeholder={lang.t("Hours or days_")}
														value={form.signUpPeriodHoursMultiplier}
														onChange={e => this.handleInputChange(e)}
													>
														<For
															each="item"
															of={this.signUpPeriodTimeTypeOptions}
															index="index"
														>
															<option
																key={`signUpPeriod_byHours${index}_${item.value}`}
																value={item.value}
															>
																{item.label}
															</option>
														</For>
													</select>
													<div>{lang.t("Time to Consider")}:</div>
													<div className="radio radio-align">
														<label htmlFor="JobAutoAssignJobCountDaysRadio-byHours">
															<input
																type="radio"
																name="JobAutoAssignYearToDateSpan"
																id="JobAutoAssignJobCountDaysRadio-byHours"
																checked={
																	!form.JobAutoAssignYearToDateSpan
																}
																value="false"
																onChange={e =>
																	this.handleRadioChange(e)
																}
															/>
														</label>
													</div>
													<input
														className={classNames("form-control", {
															"has-error":
																errors.JobAutoAssignJobCountDaysSpan,
														})}
														type="tel"
														placeholder="eg: 2"
														name="JobAutoAssignJobCountDaysSpan"
														value={form.JobAutoAssignJobCountDaysSpan}
														disabled={form.JobAutoAssignYearToDateSpan}
														onChange={e => this.handleInputChange(e)}
														onBlur={e => this.handleIntegerOnBlur(e)}
													/>
													<span>{lang.t("days")}</span>
													<div className="radio">
														<label htmlFor="JobAutoAssignYearToDateSpan-toDate">
															<input
																type="radio"
																name="JobAutoAssignYearToDateSpan"
																id="JobAutoAssignYearToDateSpan-toDate"
																checked={
																	form.JobAutoAssignYearToDateSpan
																}
																value="true"
																onChange={e =>
																	this.handleRadioChange(e)
																}
															/>
															<span>{lang.t("Year to Date")}</span>
														</label>
													</div>
												</div>
											</If>
											<If
												condition={
													form.JobAutoAssignType === SENIORITY_DESCENDING
												}
											>
												<div className="indent">
													<span>{lang.t("Sign-Up Period")}:</span>
													<input
														className={classNames("form-control", {
															"has-error":
																errors.JobAutoAssignHoursSpan,
														})}
														type="tel"
														placeholder="eg: 2"
														name="JobAutoAssignHoursSpan"
														value={form.JobAutoAssignHoursSpan}
														onChange={e => this.handleInputChange(e)}
														onBlur={e => this.handleIntegerOnBlur(e)}
													/>
													<select
														name="signUpPeriodHoursMultiplier"
														className="form-control"
														placeholder={lang.t("Hours or days_")}
														value={form.signUpPeriodHoursMultiplier}
														onChange={e => this.handleInputChange(e)}
													>
														<For
															each="item"
															of={this.signUpPeriodTimeTypeOptions}
															index="index"
														>
															<option
																key={`signUpPeriod_byHours${index}_${item.value}`}
																value={item.value}
															>
																{item.label}
															</option>
														</For>
													</select>
												</div>
											</If>
											<If
												condition={
													form.JobAutoAssignType === SENIORITY_ASCENDING
												}
											>
												<div className="indent">
													<span>{lang.t("Sign-Up Period")}:</span>
													<input
														className={classNames("form-control", {
															"has-error":
																errors.JobAutoAssignHoursSpan,
														})}
														type="tel"
														placeholder="eg: 2"
														name="JobAutoAssignHoursSpan"
														value={form.JobAutoAssignHoursSpan}
														onChange={e => this.handleInputChange(e)}
														onBlur={e => this.handleIntegerOnBlur(e)}
													/>
													<select
														name="signUpPeriodHoursMultiplier"
														className="form-control"
														placeholder={lang.t("Hours or days_")}
														value={form.signUpPeriodHoursMultiplier}
														onChange={e => this.handleInputChange(e)}
													>
														<For
															each="item"
															of={this.signUpPeriodTimeTypeOptions}
															index="index"
														>
															<option
																key={`signUpPeriod_byHours${index}_${item.value}`}
																value={item.value}
															>
																{item.label}
															</option>
														</For>
													</select>
												</div>
											</If>
											<If condition={form.JobAutoAssignType === RANDOM}>
												<div className="indent">
													<span>{lang.t("Sign-Up Period")}:</span>
													<input
														className={classNames("form-control", {
															"has-error":
																errors.JobAutoAssignHoursSpan,
														})}
														type="tel"
														placeholder="eg: 2"
														name="JobAutoAssignHoursSpan"
														value={form.JobAutoAssignHoursSpan}
														onChange={e => this.handleInputChange(e)}
														onBlur={e => this.handleIntegerOnBlur(e)}
													/>
													<select
														name="signUpPeriodHoursMultiplier"
														className="form-control"
														placeholder={lang.t("Hours or days_")}
														value={form.signUpPeriodHoursMultiplier}
														onChange={e => this.handleInputChange(e)}
													>
														<For
															each="item"
															of={this.signUpPeriodTimeTypeOptions}
															index="index"
														>
															<option
																key={`signUpPeriod_byHours${index}_${item.value}`}
																value={item.value}
															>
																{item.label}
															</option>
														</For>
													</select>
												</div>
											</If>
										</div>
									</div>
									{/* Officer Approval & Assignment Rules */}
								</Otherwise>
							</Choose>
						</div>
					</If>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						id="estimate-modal-close-modal"
						className="btn btn-link"
						onClick={() => closeModal()}
					>
						{lang.t("Cancel")}
					</button>
					<button
						type="button"
						id="estimate-modal-send-estimate"
						className="btn btn-primary"
						onClick={this.handleSave}
					>
						{lang.t("Send")}
					</button>
				</Modal.Footer>
			</Modal>
		);
	}
}

SendEstimateModal.propTypes = {
	handleEstimateModalFormChange: PropTypes.func.isRequired,
	handleJobCreation: PropTypes.func.isRequired,
	closeModal: PropTypes.func.isRequired,
	invoiceEstimate: PropTypes.object.isRequired,
	estimatePostOption: PropTypes.string.isRequired,
	requirePrepayment: PropTypes.bool.isRequired,
	user: PropTypes.object.isRequired,
	clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	jobDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	estimateTotal: PropTypes.number,
	showModal: PropTypes.bool,
	clientContacts: PropTypes.array,
	agencyAutoAssignPresets: PropTypes.object,
	jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	openQuickAddContactModal: PropTypes.func,
	getContacts: PropTypes.func,
};

SendEstimateModal.defaultProps = {
	estimateTotal: 0,
	showModal: false,
	jobDate: "",
	clientContacts: [],
	agencyAutoAssignPresets: {},
	jobId: undefined,
	openQuickAddContactModal: () => {},
	getContacts: () => {},
};

export default SendEstimateModal;
